import { apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  RecoveryDecisionHistorySchema,
  RecoveryDecisionResultSchema,
  RecoverySubjectDetailSchema,
  RecoverySubjectPageSchema,
  type RecoveryBasis,
  type RecoveryDecisionHistory,
  type RecoveryDecisionResult,
  type RecoveryStatusFilter,
  type RecoverySubjectDetail,
  type RecoverySubjectPage,
} from '@/lib/api/schemas/product-recovery.schema'

const BASE = '/api/product-recovery/subjects'

export type RecoverySubjectQuery = {
  /** Omitted means `open` on the server. `all` exists for tools, never for the screen. */
  status?: RecoveryStatusFilter
  limit?: number
  offset?: number
}

/**
 * The decision a caller may make, as three shapes rather than one with optional
 * fields.
 *
 * A basis exists only where a target does. That is not a convenience: it makes
 * it impossible to send a recognition without naming the piece it recognised, and
 * impossible to lose the basis of one that did. The union is the reason no caller
 * can collapse an exact source fact and a recognition into a single "decided".
 */
export type RecoveryDecisionRequest =
  | {
    outcome: 'matched'
    targetProductId: string
    basis: RecoveryBasis
    evidenceNote: string
  }
  | { outcome: 'not_any_of_these'; evidenceNote: string }
  | { outcome: 'cannot_determine'; evidenceNote: string }

export async function listRecoverySubjects(
  query: RecoverySubjectQuery = {},
  signal?: AbortSignal,
): Promise<RecoverySubjectPage> {
  const search = new URLSearchParams()
  if (query.status !== undefined) search.set('status', query.status)
  if (query.limit !== undefined) search.set('limit', String(query.limit))
  if (query.offset !== undefined) search.set('offset', String(query.offset))
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`
  return RecoverySubjectPageSchema.parse(
    await readApiJson(await apiFetch(`${BASE}${suffix}`, { signal })),
  )
}

/** The audit for one subject, machine decisions beside human ones. */
export async function getRecoveryDecisionHistory(
  subjectId: string,
  signal?: AbortSignal,
): Promise<RecoveryDecisionHistory> {
  return RecoveryDecisionHistorySchema.parse(
    await readApiJson(await apiFetch(`${BASE}/${encodeURIComponent(subjectId)}/decisions`, { signal })),
  )
}

export async function getRecoverySubject(
  subjectId: string,
  signal?: AbortSignal,
): Promise<RecoverySubjectDetail> {
  return RecoverySubjectDetailSchema.parse(
    await readApiJson(await apiFetch(`${BASE}/${encodeURIComponent(subjectId)}`, { signal })),
  )
}

/**
 * Record one decision.
 *
 * `decisionId` is the caller's idempotency key: a replay of the same id is the
 * same decision, so a retry after a lost response cannot record two. The actor
 * and the time are the server's, taken from the authenticated request, and are
 * never sent from here.
 *
 * `expectedRevision` is the revision the operator actually looked at. A subject
 * whose candidate set has moved since answers 409 and nothing is written.
 */
export async function recordRecoveryDecision(
  subjectId: string,
  decisionId: string,
  expectedRevision: number,
  request: RecoveryDecisionRequest,
): Promise<RecoveryDecisionResult> {
  const body = request.outcome === 'matched'
    ? {
      decisionId,
      expectedRevision,
      outcome: request.outcome,
      targetProductId: request.targetProductId,
      basis: request.basis,
      evidenceNote: request.evidenceNote,
    }
    : {
      decisionId,
      expectedRevision,
      outcome: request.outcome,
      evidenceNote: request.evidenceNote,
    }
  return RecoveryDecisionResultSchema.parse(
    await readApiJson(await apiFetch(`${BASE}/${encodeURIComponent(subjectId)}/decisions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })),
  )
}
